/**
 * Resolved chart block. ECharts uses chartTheme() hex, including dark and light.
 * Charts paint when they scroll into view and are disposed with the message.
 */
import { el } from '../../core/dom.js';
import { chartTheme } from '../../core/chart-theme.js';
import { askAboutPoint, resolvedOf } from './shape.js';
import { chartAriaLabel, chartBlockOption, labelRaw, labelText } from './chart-option.js';

const specs = new WeakMap();
let watching = false;

function echartsApi() {
  const api = globalThis.echarts;
  return api && typeof api.init === 'function' ? api : null;
}

function pointQuestion(spec, params) {
  const row = Array.isArray(params) ? params[0] : params;
  if (!row) return '';
  const label = row.name || labelText((spec.labels || [])[row.dataIndex]);
  const raw = labelRaw((spec.labels || [])[row.dataIndex]);
  const shown = raw ? `${label} (${raw})` : label;
  return askAboutPoint(row.seriesName, row.value, shown);
}

function draw(node) {
  const api = echartsApi();
  const spec = specs.get(node);
  if (!api || !spec) return;
  const existing = api.getInstanceByDom(node);
  if (existing) existing.dispose();
  const inst = api.init(node, null, { renderer: 'svg' });
  inst.setOption(chartBlockOption(spec, chartTheme(node)));
  inst.on('click', (params) => {
    const actions = specs.get(node) && node.ask;
    if (actions) actions(pointQuestion(spec, params));
  });
}

function watchTheme() {
  if (watching || typeof MutationObserver === 'undefined' || !document.documentElement) return;
  watching = true;
  new MutationObserver(() => {
    document.querySelectorAll('.chat-chart').forEach((node) => draw(node));
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  window.addEventListener('resize', () => {
    const api = echartsApi();
    if (!api) return;
    document.querySelectorAll('.chat-chart').forEach((node) => {
      const inst = api.getInstanceByDom(node);
      if (inst) inst.resize();
    });
  });
}

function whenVisible(node) {
  if (typeof IntersectionObserver !== 'function') {
    draw(node);
    return;
  }
  const io = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    draw(node);
    io.disconnect();
  }, { rootMargin: '80px' });
  io.observe(node);
}

/**
 * Paint every chart under root. The screenshot harness uses this so off-screen
 * charts are not blank; the thread still waits for IntersectionObserver.
 * @param {ParentNode} root
 */
export function paintBlockCharts(root) {
  if (!root || !root.querySelectorAll) return;
  root.querySelectorAll('.chat-chart').forEach((node) => draw(node));
}

/**
 * @param {ParentNode} root
 */
export function disposeBlockCharts(root) {
  if (!root || !root.querySelectorAll) return;
  const api = echartsApi();
  root.querySelectorAll('.chat-chart').forEach((node) => {
    specs.delete(node);
    if (!api) return;
    const inst = api.getInstanceByDom(node);
    if (inst) inst.dispose();
  });
}

function legendRow(spec) {
  const series = Array.isArray(spec.series) ? spec.series : [];
  if (series.length < 2) return null;
  return el('div', {
    class: 'chat-chart-legend',
    role: 'list',
    'aria-label': 'Series',
  }, series.map((entry, index) => el('span', {
    class: 'chat-chart-legend-item',
    role: 'listitem',
  },
  el('span', {
    class: 'chat-chart-legend-swatch',
    style: `background:var(--chart-${(index % 8) + 1})`,
    'aria-hidden': 'true',
  }),
  el('span', {}, String(entry.name || `Series ${index + 1}`)))));
}

/**
 * @param {object} block
 * @param {{ onAsk?: Function }} [actions]
 * @returns {HTMLElement}
 */
export function renderChart(block, actions) {
  const spec = resolvedOf(block);
  const node = el('div', {
    class: 'chat-chart',
    role: 'img',
    'aria-label': chartAriaLabel(spec),
  });
  specs.set(node, spec);
  node.ask = actions && actions.onAsk ? (text) => actions.onAsk(text) : null;
  whenVisible(node);
  watchTheme();
  const subtitle = spec.subtitle ? el('p', { class: 'chat-caption' }, String(spec.subtitle)) : null;
  const title = spec.title ? el('h3', { class: 'chat-block-title' }, String(spec.title)) : null;
  const plot = el('div', { class: 'chat-chart-wrap' }, legendRow(spec), node);
  return el('section', {
    class: 'chat-block chat-block-chart',
    'data-block-type': 'chart',
    'data-block-id': (block && block.id) || '',
  }, title, subtitle, plot);
}
